import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import {
  MyLeadsFilterTypes,
  MyLeadsSearchQuery,
} from '@mm/state/interfaces/myLeads';
import { ConfigProvider, Radio, RadioChangeEvent } from 'antd';
import { isEqual } from 'lodash';
import React, { useLayoutEffect, useState } from 'react';
import styles from '../MyLeadsFiltersPanel.module.css';
import { useFilterParams } from '../use-filter-params';

type Props = {
  filterParams: MyLeadsSearchQuery;
  setFilterParams: (params: MyLeadsSearchQuery) => void;
};

export const KlaviyoPurchaseFilter = ({
  filterParams,
  setFilterParams,
}: Props) => {
  const attributeKey = 'klaviyo_purchased';
  const title = 'Klaviyo Purchase Recorded';
  const options = [
    {
      value: 'purchase',
      name: 'Purchase',
    },
    {
      value: 'post_email_purchase',
      name: 'Purchase Post email-send',
    },
    {
      value: 'no_purchase',
      name: 'No purchase',
    },
  ];

  const values = {
    purchase: { klaviyo_purchased: true },
    no_purchase: { klaviyo_purchased: { filter: false, allow_nil: true } },
    post_email_purchase: { klaviyo_post_email_revenue: { gt: '0' } },
  };

  const currentValue = filterParams.query?.attributes?.klaviyo_purchased
    ? filterParams.query.attributes.klaviyo_purchased?.[0] === true
      ? 'purchase'
      : 'no_purchase'
    : filterParams.query?.attributes?.klaviyo_post_email_revenue &&
      'post_email_purchase';

  const defaultGroupValue = Object.keys(values).find(key =>
    isEqual(key, currentValue),
  );

  const { getUpdatedFilterParams, shouldDisableFilters } = useFilterParams({
    filterParams,
  });
  const { isWhitelabelCompany, primaryColor } = useWhitelabelConfig();

  const [groupValue, setGroupValue] = useState(defaultGroupValue);

  const updateFilterParams = (group: string) => {
    const attributeKey = Object.keys(values[group])[0];

    if (currentValue) {
      delete filterParams.query?.attributes?.[
        Object.keys(values[currentValue])[0]
      ];
    }

    const updatedQuery = getUpdatedFilterParams({
      key: attributeKey,
      value: values[group][attributeKey],
      isExisting: false,
      type: MyLeadsFilterTypes.attributes,
    });

    if (currentValue !== group) {
      setFilterParams(updatedQuery);
    }
  };

  const onRadioChange = (e: RadioChangeEvent) => {
    const { value } = e.target || {};
    setGroupValue(value);
    updateFilterParams(value);
  };

  useLayoutEffect(() => {
    setGroupValue(defaultGroupValue);
  }, [filterParams.query?.attributes]);

  return (
    <div className={styles.radioGroupWrapper}>
      <div className={styles.heading}>{title}</div>

      <Radio.Group onChange={onRadioChange} value={groupValue}>
        {options.map(option => {
          const isRadioChecked = isEqual(groupValue, option.value);
          return (
            <div
              className={styles.radioGroupRow}
              key={`${attributeKey}-${option.value}`}
            >
              <ConfigProvider
                theme={{
                  components: {
                    Radio: {
                      colorPrimary: isWhitelabelCompany
                        ? primaryColor
                        : 'var(--color-geekblue-6)',
                    },
                  },
                }}
              >
                <Radio
                  defaultChecked={isRadioChecked}
                  value={option.value}
                  className={styles.radio}
                  disabled={shouldDisableFilters}
                >
                  {option.name}
                </Radio>
              </ConfigProvider>
            </div>
          );
        })}
      </Radio.Group>
    </div>
  );
};
